import deep from 'deep-get-set';
import publicIp from 'react-public-ip';
import { createAction } from 'redux-api-middleware';
import { createSelector } from 'reselect';
import config from '@src/config';
import crakIds from '@src/constants/crak';
import {
  SUBMISSION_TYPE_SELECTOR_ELEMENT_TYPE_ID,
  SUBMISSION_TYPES
} from '@src/constants/element-types';
import {
  CAMPAIGN_SUCCESS_PAGE_CREATE_SUCCESS,
  CAMPAIGN_SUCCESS_PAGE_UPDATE_SUCCESS
} from '@src/redux/modules/campaigns';

// Actions
export const CAMPAIGN_INFO_LOAD = 'CAMPAIGN_INFO_LOAD';
export const CAMPAIGN_INFO_LOAD_FAILURE = 'CAMPAIGN_INFO_LOAD_FAILURE';
export const CAMPAIGN_INFO_LOAD_SUCCESS = 'CAMPAIGN_INFO_LOAD_SUCCESS';
export const CAMPAIGN_FULL_QUESTION_HIDE = 'CAMPAIGN_FULL_QUESTION_HIDE';
export const CAMPAIGN_FULL_QUESTION_SHOW = 'CAMPAIGN_FULL_QUESTION_SHOW';
export const BUCKET_UPLOAD_FAILURE = 'BUCKET_UPLOAD_FAILURE';
export const BUCKET_UPLOAD_SUCCESS = 'BUCKET_UPLOAD_SUCCESS';
export const CAMPAIGN_COUNT_LOAD = 'CAMPAIGN_COUNT_LOAD';
export const CAMPAIGN_COUNT_LOAD_SUCCESS = 'CAMPAIGN_COUNT_LOAD_SUCCESS';
export const CAMPAIGN_COUNT_LOAD_FAILURE = 'CAMPAIGN_COUNT_LOAD_FAILURE';
export const VIDEO_SELECT = 'VIDEO_SELECT';
export const VIDEO_PROCESSING_SUBMIT = 'VIDEO_PROCESSING_SUBMIT';
export const VIDEO_PROCESSING_SUBMIT_FAILURE = 'VIDEO_PROCESSING_SUBMIT_FAILURE';
export const VIDEO_PROCESSING_SUBMIT_SUCCESS = 'VIDEO_PROCESSING_SUBMIT_SUCCESS';
export const IP_ADDRESS_GET = 'IP_ADDRESS_GET';
export const IP_ADDRESS_GET_FAILURE = 'IP_ADDRESS_GET_FAILURE';
export const IP_ADDRESS_GET_SUCCESS = 'IP_ADDRESS_GET_SUCCESS';
export const CAMPAIGN_PREVIEW_LOAD = 'CAMPAIGN_PREVIEW_LOAD';
export const CAMPAIGN_PREVIEW_LOAD_FAILURE = 'CAMPAIGN_PREVIEW_LOAD_FAILURE';
export const CAMERA_RECORDER_SHOW = 'CAMERA_RECORDER_SHOW';
export const CAMERA_RECORDER_HIDE = 'CAMERA_RECORDER_HIDE';
export const UPLOADED_VIDEO_CLEAR = 'UPLOADED_VIDEO_CLEAR';
export const VIDEO_RECORDING_COMPLETE = 'VIDEO_RECORDING_COMPLETE';
export const VIDEO_POST_ACTION_CANCEL = 'VIDEO_POST_ACTION_CANCEL';
export const CAMPAIGN_ELEMENT_TYPES_LOAD = 'CAMPAIGN_ELEMENT_TYPES_LOAD';
export const CAMPAIGN_ELEMENT_TYPES_LOAD_FAILURE = 'CAMPAIGN_ELEMENT_TYPES_LOAD_FAILURE';
export const CAMPAIGN_ELEMENT_TYPES_LOAD_SUCCESS = 'CAMPAIGN_ELEMENT_TYPES_LOAD_SUCCESS';
export const CAMPAIGN_SUBMISSION_SUBMIT = 'CAMPAIGN_SUBMISSION_SUBMIT';
export const CAMPAIGN_SUBMISSION_SUBMIT_FAILURE = 'CAMPAIGN_SUBMISSION_SUBMIT_FAILURE';
export const CAMPAIGN_SUBMISSION_SUBMIT_SUCCESS = 'CAMPAIGN_SUBMISSION_SUBMIT_SUCCESS';
export const CAMPAIGN_ELEMENT_VALUE_UPDATE = 'CAMPAIGN_ELEMENT_VALUE_UPDATE';
export const MEDIA_TYPE_SET = 'MEDIA_TYPE_SET';

export const CAMPAIGN_ELEMENT_CURRENT_LINK_UPDATE = 'CAMPAIGN_ELEMENT_CURRENT_LINK_UPDATE';
export const CAMPAIGN_ELEMENT_TERMS_FETCH_LOAD = 'CAMPAIGN_ELEMENT_TERMS_FETCH_LOAD';
export const CAMPAIGN_ELEMENT_TERMS_FETCH_FAILURE = 'CAMPAIGN_ELEMENT_TERMS_FETCH_FAILURE';
export const CAMPAIGN_ELEMENT_TERMS_FETCH_SUCCESS = 'CAMPAIGN_ELEMENT_TERMS_FETCH_SUCCESS';
export const CAMPAIGN_ELEMENT_CURRENT_LINK_RESET = 'CAMPAIGN_ELEMENT_CURRENT_LINK_RESET';

export const MULTIPLE_CHOICE_SELECT_OPTION = 'MULTIPLE_CHOICE_SELECT_OPTION';

// Multipart upload additions
export const SET_IS_UPLOADING_VIDEO = 'SET_IS_UPLOADING_VIDEO';
export const SET_SELECTED_VIDEO_FILE = 'SET_SELECTED_VIDEO_FILE';
export const SET_MEDIA_SUBMISSION_ERROR = 'SET_MEDIA_SUBMISSION_ERROR';
export const SET_RETRY_UPLOAD = 'SET_RETRY_UPLOAD';

// Action creators
export const fetchCampaignTerms = (endpoint) =>
  createAction({
    endpoint,
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    types: [
      CAMPAIGN_ELEMENT_TERMS_FETCH_LOAD,
      CAMPAIGN_ELEMENT_TERMS_FETCH_SUCCESS,
      CAMPAIGN_ELEMENT_TERMS_FETCH_FAILURE
    ]
  });

export const fetchCampaignInfo = (campaignLink) =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.publicCampaignInfo}${campaignLink}?mode=landing`,
    method: 'GET',
    // X-Vp-Client-Host tells the backend which domain the visitor is actually on. This call
    // goes straight to config.apiHost with no CloudFront in between, so x-forwarded-host is
    // never populated here - the backend can't otherwise tell a white-label domain apart from
    // the default one for this request.
    headers: { 'Content-Type': 'application/json', 'X-Vp-Client-Host': window.location.host },
    types: [CAMPAIGN_INFO_LOAD, CAMPAIGN_INFO_LOAD_SUCCESS, CAMPAIGN_INFO_LOAD_FAILURE]
  });

export const fetchCampaignElementTypes = () =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.campaignElementTypes}`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    types: [
      CAMPAIGN_ELEMENT_TYPES_LOAD,
      CAMPAIGN_ELEMENT_TYPES_LOAD_SUCCESS,
      CAMPAIGN_ELEMENT_TYPES_LOAD_FAILURE
    ]
  });

export const putCampaignCount = (campaignId) =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.campaignCount}`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: campaignId
    }),
    types: [CAMPAIGN_COUNT_LOAD, CAMPAIGN_COUNT_LOAD_SUCCESS, CAMPAIGN_COUNT_LOAD_FAILURE]
  });

export const resetCurrentLinkData = () => ({
  type: CAMPAIGN_ELEMENT_CURRENT_LINK_RESET
});

export function showFullQuestion(qid) {
  return {
    type: CAMPAIGN_FULL_QUESTION_SHOW,
    qid
  };
}

export function hideFullQuestion(qid) {
  return {
    type: CAMPAIGN_FULL_QUESTION_HIDE,
    qid
  };
}

export function loadPreviewData(payload) {
  return {
    type: CAMPAIGN_PREVIEW_LOAD,
    payload
  };
}

export function setPreviewError(message) {
  return {
    type: CAMPAIGN_PREVIEW_LOAD_FAILURE,
    payload: {
      response: {
        message
      }
    }
  };
}

export function updateCampaignElementValue({
  qid,
  value,
  selected,
  attachment,
  isUploading = false,
  selectedSubmissionTypes
}) {
  return {
    type: CAMPAIGN_ELEMENT_VALUE_UPDATE,
    qid,
    value,
    selected,
    attachment,
    isUploading,
    selectedSubmissionTypes
  };
}

export const updateCampaignElementCurrentLink = (link) => ({
  type: CAMPAIGN_ELEMENT_CURRENT_LINK_UPDATE,
  link
});

export function showCameraRecorder(qid) {
  return {
    type: CAMERA_RECORDER_SHOW,
    qid
  };
}

export function hideCameraRecorder(qid) {
  return {
    type: CAMERA_RECORDER_HIDE,
    qid
  };
}

export function clearUploadedVideo(qid) {
  return {
    type: UPLOADED_VIDEO_CLEAR,
    qid
  };
}

export function completeRecording(qid) {
  return {
    type: VIDEO_RECORDING_COMPLETE,
    qid
  };
}

export function cancelPostAction(qid) {
  return {
    type: VIDEO_POST_ACTION_CANCEL,
    qid
  };
}

export function setMediaType(qid, mediaType) {
  return {
    type: MEDIA_TYPE_SET,
    qid,
    mediaType
  };
}

export function selectMultipleChoiceOption(qid, option) {
  return {
    type: MULTIPLE_CHOICE_SELECT_OPTION,
    qid,
    option
  };
}

export function setMediaSubmissionError(qid, error) {
  return {
    type: SET_MEDIA_SUBMISSION_ERROR,
    qid,
    error
  };
}

export function selectVideo(qid, objectUrl, fileType) {
  return {
    type: VIDEO_SELECT,
    qid,
    objectUrl,
    fileType
  };
}

export const submitCampaignSubmission = (campaignSubmission) =>
  createAction({
    endpoint: `${config.apiHost}${config.endpoints.campaignSubmissions}`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(campaignSubmission),
    types: [
      CAMPAIGN_SUBMISSION_SUBMIT,
      CAMPAIGN_SUBMISSION_SUBMIT_SUCCESS,
      CAMPAIGN_SUBMISSION_SUBMIT_FAILURE
    ]
  });

export const previewSubmitCampaignSubmission = (value = true) => {
  return {
    type: CAMPAIGN_SUBMISSION_SUBMIT_SUCCESS,
    value
  };
};

export const getUserIpAddress = () => async (dispatch) => {
  dispatch({ type: IP_ADDRESS_GET });

  try {
    const ip = await publicIp.v4();
    dispatch({ type: IP_ADDRESS_GET_SUCCESS, ip });
  } catch (e) {
    dispatch({ type: IP_ADDRESS_GET_FAILURE, error: e });
  }
};

export const setQuestionVideoUploadSuccess = (qid, mediaSubmissionId) => {
  return {
    type: BUCKET_UPLOAD_SUCCESS,
    meta: { qid },
    payload: { mediaSubmissionId }
  };
};

export const setQuestionVideoUploadError = (qid, error) => {
  return {
    type: BUCKET_UPLOAD_FAILURE,
    meta: { qid },
    payload: error
  };
};

export const setIsUploadingVideo = (qid, value) => {
  return {
    type: SET_IS_UPLOADING_VIDEO,
    qid,
    value
  }
};

export const setSelectedVideoFile = (qid, file) => {
  return {
    type: SET_SELECTED_VIDEO_FILE,
    qid,
    file
  }
};

export const retryUpload = (qid) => {
  return {
    type: SET_RETRY_UPLOAD,
    qid,
  }
};

// Initial state
const initialState = {
  isLoading: false,
  isLoaded: false,
  error: false,
  errorMessage: '',
  isTypesLoading: false,
  isTypesLoaded: false,
  isTypesLoadedError: false,
  isTypesLoadedErrorMessage: '',
  elementTypes: {},
  data: {},
  campaignElements: {},
  isSubmittingForProcessing: false,
  isSubmittedForProcessing: false,
  hasProcessingSubmissionError: false,
  processingSubmissionErrorMessage: '',
  currentLinkData: {},
  disableSubmitButton: false
};

// Reducer helpers
function transformCampaignElements(elements) {
  let prevId = '';
  return elements.reduce((acc, curr, idx) => {
    // Compute and ensure the id is set on the element
    prevId = curr.id || idx;
    const currId = curr.id || `${prevId}-preview-${idx}`;

    // Add extra props to curr
    curr.id = currId; // Ensure id is always set on the element object
    curr.isPreview = false;
    curr.preSignedUrlData = {};

    curr.hasUploadedVideo = false;
    curr.isUploadingVideo = false;
    curr.hasUploadFailure = false;

    curr.shouldShowCameraRecorder = false;
    curr.hasRecordedVideo = false;

    curr.shouldShowPostRecordingActions = false;

    curr.selectedVideoFile = undefined;
    curr.mediaSubmissionError = undefined;
    curr.isRetryingUpload = false;

    return {
      ...acc,
      [currId]: curr
    };
  }, {});
}

function reduceElementTypes(data) {
  return data.reduce((prev, curr) => {
    return {
      ...prev,
      [curr.id]: curr.label
    };
  }, {});
}

// Reducers
export function campaignLandingPageReducer(state = initialState, action) {
  switch (action.type) {
    case CAMPAIGN_ELEMENT_TYPES_LOAD: {
      const newState = {
        ...state,
        isTypesLoading: true,
        isTypesLoaded: false,
        isTypesLoadedError: false,
        isTypesLoadedErrorMessage: '',
        elementTypes: {}
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_TYPES_LOAD_FAILURE: {
      const newState = {
        ...state,
        isTypesLoading: false,
        isTypesLoaded: false,
        isTypesLoadedError: true,
        isTypesLoadedErrorMessage: action?.payload?.response?.message,
        elementTypes: {}
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_TYPES_LOAD_SUCCESS: {
      const newState = {
        ...state,
        isTypesLoaded: true,
        isTypesLoadedError: false,
        isTypesLoadedErrorMessage: '',
        isTypesLoading: false,
        elementTypes: reduceElementTypes(action.payload.data)
      };

      return newState;
    }

    case CAMPAIGN_INFO_LOAD: {
      const newState = {
        ...state,
        isLoading: true,
        isLoaded: false,
        error: false,
        errorMessage: '',
        data: {},
        campaignElements: {}
      };

      return newState;
    }

    case CAMPAIGN_PREVIEW_LOAD_FAILURE:
    case CAMPAIGN_INFO_LOAD_FAILURE: {
      const newState = {
        ...state,
        isLoading: false,
        isLoaded: false,
        error: true,
        errorMessage: action?.payload?.response?.message
      };

      return newState;
    }

    case CAMPAIGN_PREVIEW_LOAD: {
      const newState = {
        ...state,
        isCampaignPreview: true,
        isLoading: false,
        isLoaded: true,
        error: false,
        errorMessage: '',
        data: {
          ...action.payload,
          campaignElements: undefined // toss these out we'll move them up to the root
        },
        campaignElements: transformCampaignElements(action.payload.campaignElements)
      };

      return newState;
    }

    case CAMPAIGN_INFO_LOAD_SUCCESS: {
      const newState = {
        ...state,
        isLoading: false,
        isLoaded: true,
        error: false,
        errorMessage: '',
        data: {
          ...action.payload,
          campaignElements: undefined // toss these out we'll move them up to the root
        },
        campaignElements: transformCampaignElements(action.payload.campaignElements)
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_VALUE_UPDATE: {
      const currentElement = state.campaignElements[action.qid] || {};
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...currentElement,
            // Only update value if explicitly provided (not undefined)
            ...(action.value !== undefined && { value: action.value }),
            // Only update selected if explicitly provided (not undefined)
            ...(action.selected !== undefined && { selected: action.selected }),
            // Only update attachment if explicitly provided
            ...(action.attachment !== undefined && { attachment: action.attachment }),
            // Only update isUploading if explicitly provided
            ...(action.isUploading !== undefined && { isUploading: action.isUploading }),
            // Only update selectedSubmissionTypes if explicitly provided
            ...(action.selectedSubmissionTypes !== undefined && {
              selectedSubmissionTypes: action.selectedSubmissionTypes
            })
          }
        }
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_CURRENT_LINK_UPDATE: {
      const newState = {
        ...state,
        currentLinkData: {
          ...state?.currentLinkData,
          link: action.link
        }
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_CURRENT_LINK_RESET: {
      const newState = {
        ...state,
        currentLinkData: {}
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_TERMS_FETCH_LOAD: {
      const newState = {
        ...state,
        currentLinkData: {
          ...state?.currentLinkData,
          isLoading: true,
          error: false
        }
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_TERMS_FETCH_SUCCESS: {
      const newState = {
        ...state,
        currentLinkData: {
          ...state?.currentLinkData,
          isLoading: false,
          data: action.payload
        }
      };

      return newState;
    }

    case CAMPAIGN_ELEMENT_TERMS_FETCH_FAILURE: {
      const newState = {
        ...state,
        currentLinkData: {
          ...state?.currentLinkData,
          isLoading: false,
          error: true
        }
      };

      return newState;
    }

    case CAMPAIGN_FULL_QUESTION_SHOW: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            isPreview: false
          }
        }
      };

      return newState;
    }

    case CAMPAIGN_FULL_QUESTION_HIDE: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            isPreview: true
          }
        }
      };

      return newState;
    }

    case CAMERA_RECORDER_SHOW: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            shouldShowCameraRecorder: true,
            // Reset video upload reference
            hasUploadedVideo: false,
            selectedVideoFile: undefined
          }
        }
      };

      return newState;
    }

    case CAMERA_RECORDER_HIDE: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            shouldShowCameraRecorder: false
          }
        }
      };

      return newState;
    }

    case BUCKET_UPLOAD_SUCCESS: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.meta.qid]: {
            ...state.campaignElements[action.meta.qid],
            hasUploadedVideo: true,
            hasUploadFailure: false,
            // Let's close the "accordion" on upload success
            isPreview: true,
            isUploading: false,
            shouldShowCameraRecorder: false,
            shouldShowPostRecordingActions: false,
            mediaSubmissionId: action?.payload?.mediaSubmissionId
          }
        }
      };
      return newState;
    }

    case BUCKET_UPLOAD_FAILURE: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.meta.qid]: {
            ...state.campaignElements[action.meta.qid],
            hasUploadedVideo: false,
            hasUploadFailure: true,
            isUploading: false,
            bucketUploadError: action.payload
          }
        }
      };
      return newState;
    }

    case VIDEO_SELECT: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            videoPreview: action.objectUrl,
            mediaType: action.fileType
          }
        }
      };

      return newState;
    }

    case CAMPAIGN_SUBMISSION_SUBMIT: {
      const newState = {
        ...state,
        isSubmittingForProcessing: true,
        hasProcessingSubmissionError: false,
        processingSubmissionErrorMessage: ''
      };
      return newState;
    }
    case CAMPAIGN_SUBMISSION_SUBMIT_FAILURE: {
      const newState = {
        ...state,
        isSubmittingForProcessing: false,
        isSubmittedForProcessing: false,
        hasProcessingSubmissionError: true,
        processingSubmissionErrorMessage: action?.payload?.response?.message
      };

      return newState;
    }

    case CAMPAIGN_SUBMISSION_SUBMIT_SUCCESS: {
      const newState = {
        ...state,
        isSubmittingForProcessing: false,
        isSubmittedForProcessing: true
      };

      return newState;
    }

    case IP_ADDRESS_GET_SUCCESS: {
      const newState = {
        ...state,
        data: {
          ...state.data,
          ipAddress: action.ip
        }
      };

      return newState;
    }

    case UPLOADED_VIDEO_CLEAR: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            hasUploadedVideo: false,
          }
        }
      };

      return newState;
    }

    case VIDEO_RECORDING_COMPLETE: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            hasRecordedVideo: true,
            shouldShowPostRecordingActions: true,
          }
        }
      };

      return newState;
    }

    case VIDEO_POST_ACTION_CANCEL: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            hasRecordedVideo: false,
            shouldShowPostRecordingActions: false,
            shouldShowCameraRecorder: false,
            hasUploadFailure: false,
            hasSigningError: false,
            selectedVideoFile: undefined
          }
        }
      };

      return newState;
    }

    case CAMPAIGN_SUCCESS_PAGE_CREATE_SUCCESS:
    case CAMPAIGN_SUCCESS_PAGE_UPDATE_SUCCESS: {
      const newState = {
        ...state,
        data: {
          ...state.data,
          campaignSuccessPage: action.payload
        }
      };
      return newState;
    }

    case MULTIPLE_CHOICE_SELECT_OPTION: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            value: action.option
          }
        }
      };

      return newState;
    }

    case SET_IS_UPLOADING_VIDEO: {
      const isUploadingVideo = action.value;

      // reset error state only if we're setting isUploadingVideo to true
      // otherwise keep previous state
      const hasUploadFailure = isUploadingVideo
        ? false
        : state.campaignElements[action.qid].hasUploadFailure;
      const bucketUploadError = isUploadingVideo
        ? undefined
        : state.campaignElements[action.qid].bucketUploadError;

      const newState = {
        ...state,
        disableSubmitButton: action.value, // prevent submission during upload
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            isUploadingVideo: action.value,
            isRetryingUpload: false,
            hasUploadFailure,
            bucketUploadError
          }
        }
      };

      return newState;
    }

    case SET_SELECTED_VIDEO_FILE: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            selectedVideoFile: action.file
          }
        }
      };

      return newState;
    }

    case SET_MEDIA_SUBMISSION_ERROR: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            mediaSubmissionError: action.error
          }
        }
      }

      return newState;
    }

    case SET_RETRY_UPLOAD: {
      const newState = {
        ...state,
        campaignElements: {
          ...state.campaignElements,
          [action.qid]: {
            ...state.campaignElements[action.qid],
            isRetryingUpload: true
          }
        }
      }

      return newState;
    }

    default: {
      return state;
    }
  }
}

// Selectors
export const getCampaignLandingState = createSelector(
  (state) => state,
  (state) => state.get('campaignLanding')
);

export const getIsCampaignLandingLoading = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'isLoading')
);

export const getIsCampaignLandingLoaded = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'isLoaded')
);

export const getCampaignLandingHasError = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'error')
);

export const getCampaignLandingErrorText = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'errorMessage')
);

export const getCampaignLandingData = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'data')
);

export const getCampaignName = createSelector(
  getCampaignLandingData,
  (data) => deep(data, 'name') || ''
);

export const getCampaignBrandingData = createSelector(
  getCampaignLandingData,
  (getCampaignLandingData) => deep(getCampaignLandingData, 'campaignBranding')
);

export const getCampaignBanner = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'banner')
);

export const getUseCampaignBanner = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'useBanner')
);

export const getCampaignLogoUrl = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'logo') || ''
);

export const getUseCampaignLogo = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'useLogo') || ''
);

export const getCampaignProfile = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'profile') || ''
);

export const getUseCampaignProfile = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'useProfile') || ''
);

export const getUseCampaignProfileSection = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) =>
    !!deep(getCampaignBrandingData, 'useProfile') ||
    !!deep(getCampaignBrandingData, 'name') ||
    !!deep(getCampaignBrandingData, 'title')
);

export const getCampaignBrandName = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'name') || ''
);

export const getCampaignBrandTitle = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'title') || ''
);

export const getCampaignBrandFontColor = createSelector(
  getCampaignBrandingData,
  (getCampaignBrandingData) => deep(getCampaignBrandingData, 'fontColor') || ''
);

export const getCampaignId = createSelector(
  getCampaignLandingData,
  (data) => deep(data, 'campaignId') || null
);

export const getCampaignDescription = createSelector(
  getCampaignLandingData,
  (data) => deep(data, 'description') || ''
);

export const getIpAddress = createSelector(
  getCampaignLandingData,
  (data) => deep(data, 'ipAddress') || '0.0.0.0'
);

export const getCustomCampaignSuccessPageData = createSelector(
  getCampaignLandingData,
  (data) => deep(data, 'campaignSuccessPage') || null
);

export const getIsCampaignDisabled = createSelector(
  getCampaignLandingData,
  (data) => !!deep(data, 'disabled') || false
);

export const getCampaignEndDate = createSelector(
  getCampaignLandingData,
  (data) => deep(data, 'endDate') ?? null
);

export const getIsCampaignExpired = (state) => {
  const endDate = deep(getCampaignLandingData(state), 'endDate');
  return endDate ? new Date() > new Date(endDate) : false;
};

export const getCampaignElements = createSelector(getCampaignLandingState, (campaignLandingState) =>
  deep(campaignLandingState, 'campaignElements')
);

export const getHasTermsSignatureElement = createSelector(
  getCampaignElements,
  (campaignElements) => {
    const campaignElementValues = campaignElements || {};
    for (const prop in campaignElementValues) {
      if (campaignElementValues[prop]?.crakId === crakIds.SIGNATURE) {
        return true;
      }
    }
    return false;
  }
);

export const getCampaignElementTypes = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'elementTypes')
);

export const getCampaignElementIds = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => Object.keys(campaignLandingState.campaignElements)
);

export const getCampaignLandingOrganizationId = createSelector(
  getCampaignLandingData,
  (data) => data?.organizationId
);

export const getCampaignElementById = createSelector(
  (state) => getCampaignLandingState(state),
  (state, questionId) => questionId,
  (state, questionId) => {
    const campaignElements = deep(state, 'campaignElements') || {};

    return campaignElements[questionId];
  }
);

export const getTermsConditionsElementId = createSelector(
  getCampaignElements,
  (campaignElements) => {
    const campaignElementValues = campaignElements || {};
    for (const prop in campaignElementValues) {
      if (campaignElementValues[prop]?.crakId === crakIds.TERMS_AND_CONDITIONS_INPUT) {
        return prop;
      }
    }
    return '';
  }
);

export const getIsQuestionPreview = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'isPreview')
);

export const getBucketUploadError = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'bucketUploadError.message') || null
);

export const getMediaSubmissionIdForElement = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'mediaSubmissionId') || null
);

export const getHasUploadedVideoForQuestionId = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'hasUploadedVideo')
);

export const getIsVideoUploadingToBucket = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'isUploading')
);

export const getHasVideoBucketUploadFailed = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'hasUploadFailure')
);

export const getVideoPreview = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'videoPreview')
);

// Helper function to validate submission type selector element
const isSubmissionTypeSelectorValid = (element) => {
  const selectedTypes = element.selectedSubmissionTypes || [];

  // Must have at least one type selected
  if (!Array.isArray(selectedTypes) || selectedTypes.length === 0) {
    return false;
  }

  const collectorData = element.value || {};

  // Validate each selected type has its required content
  for (const type of selectedTypes) {
    switch (type) {
      case SUBMISSION_TYPES.VIDEO:
        // Video requires mediaSubmissionId or hasUploadedVideo (set by video upload flow)
        if (!element.mediaSubmissionId && !element.hasUploadedVideo) {
          return false;
        }
        break;
      case SUBMISSION_TYPES.IMAGE:
        // Image requires attachment
        if (!collectorData.image?.attachment) {
          return false;
        }
        break;
      case SUBMISSION_TYPES.TEXT:
        // Text requires non-empty content
        if (!collectorData.text?.textContent?.trim()) {
          return false;
        }
        break;
      default:
        break;
    }
  }

  return true;
};

export const getReadyToSubmit = createSelector(getCampaignLandingState, (campaignLandingState) => {
  const elementsObject = deep(campaignLandingState, 'campaignElements') || {};
  const elements = Object.values(elementsObject);
  for (let element of elements) {
    // Check if element is still uploading
    if (element.isUploading) {
      return false;
    }

  // Special handling for submission type selector
  if (element.elementTypeId === SUBMISSION_TYPE_SELECTOR_ELEMENT_TYPE_ID) {
    // Always validate selection and required content for chosen types
    if (!isSubmissionTypeSelectorValid(element)) {
      return false;
    }
    continue;
  }

    // Standard validation for other elements
    if (
      element.required &&
      !(
        element.value ||
        element.hasUploadedVideo ||
        element.selected ||
        element.attachment
      )
    ) {
      return false;
    }
  }

  return true;
});

export const getIsSubmittingForProcessing = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'isSubmittingForProcessing')
);

export const getHasSubmittedForProcessing = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'isSubmittedForProcessing')
);

export const getHasLoadingError = createSelector(getCampaignLandingState, (campaignLandingState) =>
  deep(campaignLandingState, 'error')
);

export const getHasProcessingSubmissionError = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'hasProcessingSubmissionError')
);

export const getProcessingSubmissionErrorMessage = createSelector(
  getCampaignLandingState,
  (state) => state?.processingSubmissionErrorMessage
);

export const getLoadingErrorMessage = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'errorMessage')
);

export const getShouldShowCameraRecorder = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'shouldShowCameraRecorder')
);

export const getShouldShowPostRecordingActions = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'shouldShowPostRecordingActions')
);

export const getMediaTypeForQid = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (question) => deep(question, 'mediaType')
);

export const getVideoDesignElementUrl = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (element) => deep(element, 'media.rawPath') || null
);

export const getSelectedVideoFile = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (element) => deep(element, 'selectedVideoFile')
);

export const getIsUploadingVideo = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (element) => deep(element, 'isUploadingVideo')
);

export const getMediaSubmissionError = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (element) => deep(element, 'mediaSubmissionError')
);

export const getIsRetryingUpload = createSelector(
  (state, questionId) => getCampaignElementById(state, questionId),
  (element) => deep(element, 'isRetryingUpload')
);

export const getIsCampaignPreview = createSelector(
  getCampaignLandingState,
  (campaignLandingState) => deep(campaignLandingState, 'isCampaignPreview')
);

export const getCurrentLinkData = createSelector(
  getCampaignLandingState,
  (state) => state?.currentLinkData
);

export const getCampaignCustomDomain = createSelector(
  getCampaignLandingState,
  (state) => state?.data?.campaign_domain?.domain
);

export const getIsClpDownloadEnabled = createSelector(
  getCampaignLandingState,
  (state) => state?.data?.isDownloadAllowed
);

export const getCampaignLanguage = createSelector(
  getCampaignLandingState,
  (state) => state?.data?.language
);

export const getDisableSubmitButton = createSelector(
  (state) => state.get('campaignLanding'),
  (state) => state?.disableSubmitButton
);

export const getCampaignAutoTags = createSelector(
  getCampaignLandingState,
  (state) => state?.data?.autotags
);

export const getCampaignRewardEnabled = createSelector(
  getCampaignLandingData,
  (data) => {
    const rewardSettings = deep(data, 'rewardSettings');
    const enabled = rewardSettings?.enabled;
    return enabled === true || enabled === 1 || enabled === '1';
  }
);

export const getCampaignRewardAmount = createSelector(
  getCampaignLandingData,
  (data) => {
    const rewardSettings = deep(data, 'rewardSettings');
    const amount = rewardSettings?.amount || 0;
    const numericAmount = Number(amount);
    return !isNaN(numericAmount) && numericAmount > 0 ? numericAmount : 0;
  }
);



// WEBPACK FOOTER //
// ./src/redux/modules/campaignLandingPage.js